import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Container, Tabs, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import PageListHeader from "../../components/pageListHeader";
import AccountingReportFilters from "../../components/accounting/AccountingReportFilters";
import ProfitAndLossView from "../../components/accounting/ProfitAndLossView";
import BalanceSheetView from "../../components/accounting/BalanceSheetView";
import {
  defaultPeriodEndDate,
  downloadBalanceSheetCsv,
  downloadProfitAndLossCsv,
  formatDateLabel,
  hasBalanceSheetActivity,
  hasPnlActivity,
} from "../../components/accounting/financialStatementsUtils";

const FinancialStatements = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const [pnl, setPnl] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [fiscalPeriodId, setFiscalPeriodId] = useState(null);
  const [periodName, setPeriodName] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [bsAsOfDate, setBsAsOfDate] = useState(null);
  const [activeTab, setActiveTab] = useState("pnl");
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bsAsOfOverrideRef = useRef(false);

  const loadReports = async (overrides = {}) => {
    const effectiveStart = overrides.startDate ?? startDate;
    const effectiveEnd = overrides.endDate ?? endDate;
    const effectiveBsAsOf = overrides.bsAsOfDate ?? bsAsOfDate;

    setLoading(true);
    try {
      const pnlQuery = {};
      const bsQuery = {};
      if (fiscalPeriodId) {
        pnlQuery.fiscalPeriodId = fiscalPeriodId;
        bsQuery.fiscalPeriodId = fiscalPeriodId;
      }
      if (effectiveStart) pnlQuery.startDate = effectiveStart.toISOString();
      if (effectiveEnd) pnlQuery.endDate = effectiveEnd.toISOString();
      if (effectiveBsAsOf) bsQuery.endDate = effectiveBsAsOf.toISOString();
      else if (effectiveEnd) bsQuery.endDate = effectiveEnd.toISOString();

      const [profitAndLoss, sheet] = await Promise.all([
        api.Accounting.getProfitAndLoss(pnlQuery),
        api.Accounting.getBalanceSheet(bsQuery),
      ]);
      setPnl(profitAndLoss);
      setBalanceSheet(sheet);
      setLoaded(true);
    } catch (error) {
      showNotification({
        color: "red",
        title: t("accounting.financialStatements.loadFailed"),
        message: error?.message || t("accounting.financialStatements.loadFailedMessage"),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!fiscalPeriodId) return;

    const initPeriod = async () => {
      setLoading(true);
      try {
        const period = await api.getById({
          apiEntity: "fiscalPeriod",
          id: fiscalPeriodId,
        });
        setPeriodName(period?.name || "");
        const periodStart = period?.startDate ? new Date(period.startDate) : null;
        const periodEnd = defaultPeriodEndDate(period?.endDate);
        setStartDate(periodStart);
        setEndDate(periodEnd);
        const nextBsAsOf = periodEnd;
        if (!bsAsOfOverrideRef.current) {
          setBsAsOfDate(nextBsAsOf);
        }

        const pnlQuery = { fiscalPeriodId };
        const bsQuery = { fiscalPeriodId };
        if (periodStart) pnlQuery.startDate = periodStart.toISOString();
        if (periodEnd) pnlQuery.endDate = periodEnd.toISOString();
        if (nextBsAsOf) bsQuery.endDate = nextBsAsOf.toISOString();
        else if (periodEnd) bsQuery.endDate = periodEnd.toISOString();

        const [profitAndLoss, sheet] = await Promise.all([
          api.Accounting.getProfitAndLoss(pnlQuery),
          api.Accounting.getBalanceSheet(bsQuery),
        ]);
        setPnl(profitAndLoss);
        setBalanceSheet(sheet);
        setLoaded(true);
      } catch (error) {
        setPeriodName("");
        showNotification({
          color: "red",
          title: t("accounting.financialStatements.loadFailed"),
          message: error?.message || t("accounting.financialStatements.loadFailedMessage"),
        });
      } finally {
        setLoading(false);
      }
    };

    bsAsOfOverrideRef.current = false;
    initPeriod();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalPeriodId]);

  const handlePnlEndDateChange = useCallback((value) => {
    setEndDate(value);
    if (!bsAsOfOverrideRef.current) {
      setBsAsOfDate(value);
    }
  }, []);

  const handleBsAsOfDateChange = useCallback((value) => {
    bsAsOfOverrideRef.current = Boolean(value);
    setBsAsOfDate(value);
  }, []);

  const periodLabel = useMemo(() => {
    if (activeTab === "pnl") {
      if (!startDate && !endDate) return "";
      return t("accounting.financialStatements.periodRange", {
        from: formatDateLabel(startDate) || "—",
        to: formatDateLabel(endDate) || "—",
      });
    }
    const asOf = bsAsOfDate || endDate;
    return t("accounting.financialStatements.asOfLabel", {
      date: formatDateLabel(asOf) || "—",
    });
  }, [activeTab, bsAsOfDate, endDate, startDate, t]);

  const canDownloadPnl = Boolean(pnl && hasPnlActivity(pnl));
  const canDownloadBs = Boolean(balanceSheet && hasBalanceSheetActivity(balanceSheet));
  const canDownload = activeTab === "pnl" ? canDownloadPnl : canDownloadBs;

  const handleDownload = useCallback(() => {
    if (activeTab === "pnl") {
      if (!canDownloadPnl) {
        showNotification({
          color: "yellow",
          title: t("accounting.financialStatements.downloadFailed"),
          message: t("accounting.financialStatements.noActivity"),
        });
        return;
      }
      downloadProfitAndLossCsv({
        report: pnl,
        periodName,
        startDate,
        endDate,
        t,
      });
      return;
    }

    if (!canDownloadBs) {
      showNotification({
        color: "yellow",
        title: t("accounting.financialStatements.downloadFailed"),
        message: t("accounting.financialStatements.noActivity"),
      });
      return;
    }
    downloadBalanceSheetCsv({
      report: balanceSheet,
      periodName,
      bsAsOfDate: bsAsOfDate || endDate,
      t,
    });
  }, [
    activeTab,
    balanceSheet,
    bsAsOfDate,
    canDownloadBs,
    canDownloadPnl,
    endDate,
    periodName,
    pnl,
    startDate,
    t,
  ]);

  const actionButtons = useMemo(
    () => [
      {
        label: t("Download"),
        variant: "default",
        disabled: !canDownload,
        onClick: handleDownload,
      },
      {
        label: t("Refresh"),
        variant: "default",
        loading,
        onClick: loadReports,
      },
      {
        label: t("Close"),
        variant: "default",
        onClick: () => tabNav.closeActive(),
      },
    ],
    [canDownload, handleDownload, loading, t, tabNav]
  );

  return (
    <Container size="xl">
      <PageListHeader
        title={t("accounting.modules.financialStatements.label")}
        actionButtons={actionButtons}
      />
      <Text size="sm" color="dimmed" mb="md">
        {t("accounting.financialStatements.pageIntro")}
      </Text>
      <AccountingReportFilters
        fiscalPeriodId={fiscalPeriodId}
        onFiscalPeriodChange={setFiscalPeriodId}
        showPnlDateRange
        pnlStartDate={startDate}
        onPnlStartDateChange={setStartDate}
        pnlEndDate={endDate}
        onPnlEndDateChange={handlePnlEndDateChange}
        showBsAsOfDate
        bsAsOfDate={bsAsOfDate}
        onBsAsOfDateChange={handleBsAsOfDateChange}
      />
      {!fiscalPeriodId && !loading && (
        <Text size="sm" color="dimmed" mb="md">
          {t("accounting.financialStatements.selectPeriodHint")}
        </Text>
      )}
      <Tabs value={activeTab} onTabChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="pnl">
            {t("accounting.financialStatements.tabPnl")}
          </Tabs.Tab>
          <Tabs.Tab value="balanceSheet">
            {t("accounting.financialStatements.tabBalanceSheet")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="pnl">
          <ProfitAndLossView
            report={pnl}
            loading={loading || (!loaded && Boolean(fiscalPeriodId))}
            periodLabel={periodLabel}
          />
        </Tabs.Panel>

        <Tabs.Panel value="balanceSheet">
          <BalanceSheetView
            report={balanceSheet}
            loading={loading || (!loaded && Boolean(fiscalPeriodId))}
            periodLabel={periodLabel}
          />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
};

export default FinancialStatements;
